// useIntlAccaSlip — a single slip with its legs + parent tournament.
// Binds SlipDetailPage / SlipDetailDocumentPage's DATA BINDING
// CONTRACT. DOCTRINE: never fabricate — on error/empty every field is
// null and the page renders its honest-empty state.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import type {
  IntlAccaSlip,
  IntlAccaSlipLeg,
  IntlAccaSlipWithLegs,
  IntlAccaTournament,
} from "../db/intl-acca-types.ts";

export interface IntlAccaSlipVM {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  slip: IntlAccaSlipWithLegs | null;
  matchLabels?: Record<string, string>;
  refetch: () => Promise<void>;
}

export function useIntlAccaSlip(slipId: string): IntlAccaSlipVM {
  const supabase = useMemo(() => getBrowserClient() as any, []);
  const [s, setS] = useState<Omit<IntlAccaSlipVM, "refetch">>({
    loading: true, error: null, tournament: null, slip: null,
  });

  const fetchAll = useCallback(async () => {
    if (!slipId) {
      setS({ loading: false, error: null, tournament: null, slip: null });
      return;
    }
    setS((p) => ({ ...p, loading: true, error: null }));
    try {
      const [slRes, lgRes] = await Promise.all([
        supabase.from("intl_acca_slips").select("*").eq("id", slipId).maybeSingle(),
        supabase.from("intl_acca_slip_legs").select("*")
          .eq("slip_id", slipId).order("created_at"),
      ]);
      if (slRes.error) throw slRes.error;
      const slip = slRes.data as IntlAccaSlip | null;
      if (!slip) {
        setS({ loading: false, error: null, tournament: null, slip: null });
        return;
      }
      const tRes = await supabase.from("intl_acca_tournaments")
        .select("*").eq("id", slip.tournament_id).maybeSingle();
      setS({
        loading: false, error: null,
        tournament: (tRes.data as IntlAccaTournament | null) ?? null,
        slip: { ...slip, legs: (lgRes.data ?? []) as IntlAccaSlipLeg[] },
      });
    } catch (e) {
      setS({
        loading: false, error: e instanceof Error ? e.message : String(e),
        tournament: null, slip: null,
      });
    }
  }, [supabase, slipId]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);
  return { ...s, refetch: fetchAll };
}
